import axios from "axios"
import {Storage} from "@/services/Storage";
import {hardwareBridge} from "@/services/hardwareBridge";
import {responses} from "@/utils/responses";

/**
 * Tolerant response parser for the Laravel stocktake POST endpoint.
 *
 * The backend (Laravel `OnlinSalesServerV2`) currently returns malformed JSON
 * with PHP-style single-quoted values: `{"id":'44'}` (single quotes around the id
 * value). Valid JSON requires double quotes; this form is rejected by `JSON.parse`
 * and causes axios to surface the raw string in `resp.data` instead of an object.
 *
 * This helper extracts the stock-take id from EITHER form:
 *   1. Already-parsed object  → `resp.data.id`
 *   2. Properly quoted JSON   → `JSON.parse(raw).id`
 *   3. PHP-style single-quote → regex match `{"id":'(\d+)'}` → returns the digits
 *
 * TODO(backend-ticket): remove this workaround once `OnlinSalesServerV2` returns
 * `{"id":"44"}` instead of `{"id":'44'}`. See Hermes ticket #ST-BE-001.
 *
 * @param {*} raw - Either a parsed object or a raw string response body
 * @returns {string|number|undefined} The extracted id, or undefined if not found
 */
function parseStockTakeId(raw) {
    if (raw != null && typeof raw === 'object' && 'id' in raw) {
        return raw.id;
    }
    if (typeof raw !== 'string') return undefined;
    // Strict JSON first
    try {
        const parsed = JSON.parse(raw);
        return parsed?.id;
    } catch (_) {
        // Tolerant: handles {"id":'44'}, {"id":"44"}, {"id":44}, with optional spaces
        const match = raw.match(/\{["']?id["']?\s*:\s*['"]?([0-9]+)["']?\s*\}/);
        return match ? match[1] : undefined;
    }
}

export default {
    name: 'stockTake',
    state: {
        stockTakeList: [],
        stockTakeItems: [],
        stockTakeRoute: '/stock-take',
    },
    getters: {
        getStockTakeList: (state) => state.stockTakeList,
        getStockTakeRoute: (state) => state.stockTakeRoute,
        getStockTakeItems: (state) => state.stockTakeItems,
    },

    mutations: {
        setStockTakeList: (state, data) => state.stockTakeList = data,
        setStockTakeRoute: (state, data) => state.stockTakeRoute = data,
        setStockTakeItems: async (state, data) => {
            state.stockTakeItems = data.map(item=>{
                item.countedQty = '';
                return item;
            })
        },
    },
    actions: {
        async getStockTakeList(context) {
            let store = await Storage.getDefaultStore();
            return axios.get(franchiseTomcatApi + `savedstocktakes/${store.StoreDB}/getAll`)
                .then(({data}) => context.commit('setStockTakeList', data))
                .catch((err) => errorHandler.tomcatError(err))
        },

        async getStockTakeItemsList(context, id) {
            let store = await Storage.getDefaultStore();
            return axios.get(franchiseTomcatApi + `stocktakesavedlist/${store.StoreDB}/getPluByID/${id}`)
                .then(({data}) => context.commit('setStockTakeItems', data))
                .catch((err) => errorHandler.tomcatError(err))

        },
        getstock: async (context, payload) => {
            let store = await Storage.getDefaultStore();
            const globalDateRange = context.getters.getGlobalDateRange;

            // Construct query parameters from globalDateRange
            const queryParams = new URLSearchParams({
                user: 'All',
                limit: 5000000,
                ...globalDateRange
            }).toString();

            return axios.get(`${franchiseApi}StockTake/getAll/${store.StoreDB}?${queryParams}`)
                .then(({data}) => data)
                .catch(err => errorHandler.tomcatError(err));
        },

        getStockByDepartment: async (context, payload) => {
            let store = await Storage.getDefaultStore();
            return axios.get(franchiseTomcatApi + `stock/${store.StoreDB}/getByDepartment`, {params: payload})
                .then(({data}) => data).catch(err => errorHandler.tomcatError(err));
        },

        async finalizeStockTake(context) {
            let store = await Storage.getDefaultStore();
            let user = await Storage.getCurrentUser();
            let settings = await Storage.getDeviceSettings();
            let pc = settings.PC;
            let items = context.getters.getStockTakeItems
                .filter(item=> item.countedQty !== '')
                .map(item => {
                    return {
                        'plu': item.PLU,
                        'counted': parseFloat(item.countedQty),
                    }
                });
            let payload = {
                user: user.Username,
                PC : pc,
                items: items
            };
            console.log('[ST] finalizeStockTake: starting POST', { url: serverApi + `stocktake/${store.StoreDB}`, payload });
            return axios.post(serverApi + `stocktake/${store.StoreDB}`, payload)
                .then((resp) => {
                    const rawBody = resp.data;
                    console.log('[ST] finalizeStockTake: raw response', { rawBody, typeOfRawBody: typeof rawBody });
                    const parsedId = parseStockTakeId(rawBody);
                    console.log('[ST] finalizeStockTake: parsed id', { parsedId, typeOfId: typeof parsedId });
                    if (parsedId == null) {
                        console.error('[ST] finalizeStockTake: could not extract id from response');
                        throw new Error('Stock take saved but no id returned by server');
                    }
                    axios.post(franchiseTomcatApi + `emailrecord/${store.StoreDB}/emailStockTake`, { stockTakeID: parsedId });
                    context.commit('setStockTakeItems', []);
                    context.commit('setStockTakeRoute', '/stock-take');
                    context.dispatch('getAllStock');
                    console.log('[ST] finalizeStockTake: returning id', parsedId);
                    return parsedId;
                }).catch((err) => {
                    console.error('[ST] finalizeStockTake: AXIOS ERROR', err);
                    return errorHandler.tomcatError(err);
                })
        },

        /**
         * Prints a saved stock take via thermal printer using OnlinePrintService.
         * Backend now does the data fetch itself (matches printGRV pattern), so this action
         * is a thin client: posts device settings, receives a base64 ESC/POS payload,
         * then routes it to the configured printer.
         * Requires Main Receipt Printer configured in device settings.
         *
         * Endpoint: POST ${printVoid}printing/${storeDB}/printStockTake/${stockTakeID}
         * Body: { settings } (the previous stockTakeData envelope is no longer needed as of 2026-07-16)
         * Response: raw base64 string of ESC/POS bytes.
         *
         * @param {object} context - Vuex action context (unused here, kept for module convention)
         * @param {string|number} stockTakeID - The StkNum of the saved stock take to print
         * @returns {Promise<boolean|undefined>} True on successful Electron print, undefined on skip/fail
         */
        printStockTake: async (context, stockTakeID) => {
            let store = await Storage.getDefaultStore();
            let settings = await Storage.getDeviceSettings();

            try {
                const response = await axios.post(
                    `${printVoid}printing/${store.StoreDB}/printStockTake/${stockTakeID}`,
                    { settings }
                );

                if (androidMode) {
                    return window.location.href = `rawbt:base64,${response.data}`;
                }

                if (settings.Printer) {
                    if (window?.hardwareIPC || window?.electronBridge) {
                        const ok = await hardwareBridge.printBase64({
                            printer: settings.Printer,
                            base64: response.data
                        });
                        if (!ok) return responses.showWarning('Failed to send print to Electron printer');
                        return true;
                    }
                    return responses.showWarning('No supported print channel available');
                } else {
                    return responses.showWarning('No printer selected');
                }
            } catch (err) {
                return errorHandler.tomcatError(err, false, 'Stock Take Print Error');
            }
        },
    }
}
